import { PAIR_PAGE_TYPE } from '@/constants/global';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { IMarketPair } from '@/features/market/types';
import { useFavMarketPairs } from '@/hooks/useFavorites';
import { useSideNavigate } from '@/hooks/useRouterNavigate';
import { useChainId } from '@/hooks/web3/useChain';
import ArrowLeft from '@/pages/components/PairInfo/assets/icon_chevron_select_12.svg?react';
import { useDebounceFn } from 'ahooks';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TokenLogo from '../TokenLogo';
import TokenPairSymbol from '../TokenPair/TokenPairSymbol';
import FavIcon from './assets/fav.svg?react';
import './index.less';

interface FavPairsProps {
  className?: string;
  onPairSelect?: (pair: IMarketPair) => void;
  maxItems?: number;
}
const FavPairs: React.FC<FavPairsProps> = ({ className = '', onPairSelect, maxItems = 10 }) => {
  const chainId = useChainId();

  const favPairs = useFavMarketPairs(chainId);
  const { pairPageNavigate } = useSideNavigate();
  const displayPairs = useMemo(() => {
    if (!favPairs) return [];
    return favPairs.slice(0, maxItems);
  }, [favPairs, maxItems]);

  // Arrow navigation state
  const [widthDifference, setWidthDifference] = useState(0);
  const [distanceToLeft, setDistanceToLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  // Calculate width difference for arrow navigation
  const calculateWidthDifference = useCallback(() => {
    if (containerRef.current && listRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const listWidth = listRef.current.scrollWidth;
      const difference = listWidth - containerWidth;
      setWidthDifference(difference > 0 ? difference : 0);
    }
  }, []);

  // Debounced resize handler
  const { run: debouncedCalculateWidth } = useDebounceFn(calculateWidthDifference, { wait: 100 });

  useEffect(() => {
    calculateWidthDifference();
    window.addEventListener('resize', debouncedCalculateWidth);
    return () => {
      window.removeEventListener('resize', debouncedCalculateWidth);
    };
  }, [calculateWidthDifference, debouncedCalculateWidth, displayPairs]);

  // Arrow navigation handlers
  const moveLeft = useCallback(() => {
    const newDistance = Math.max(0, distanceToLeft - 200);
    setDistanceToLeft(newDistance);
  }, [distanceToLeft]);

  const moveRight = useCallback(() => {
    const newDistance = Math.min(widthDifference, distanceToLeft + 200);
    setDistanceToLeft(newDistance);
  }, [distanceToLeft, widthDifference]);

  const handlePairClick = (pair: IMarketPair) => {
    onPairSelect?.(pair);
    pairPageNavigate(
      pair.symbol,
      location.pathname.includes(PAIR_PAGE_TYPE.EARN) ? PAIR_PAGE_TYPE.EARN : PAIR_PAGE_TYPE.TRADE,
    );
  };

  const containerClass = classNames('syn-fav-pairs', className);

  if (!displayPairs.length) {
    return null;
  }

  const showLeftArrow = widthDifference > 0 && distanceToLeft > 0;
  const showRightArrow = widthDifference > 0 && distanceToLeft < widthDifference;

  return (
    <div className={containerClass}>
      <div className="syn-fav-pairs__icon">
        <FavIcon />
      </div>
      <div className="syn-fav-pairs__divider" />

      <div className={classNames('syn-fav-pairs__list-container')} ref={containerRef}>
        {/* Left Arrow */}
        {showLeftArrow && (
          <div className="syn-fav-pairs__arrow left" onClick={moveLeft}>
            <ArrowLeft />
          </div>
        )}

        {/* Right Arrow */}
        {showRightArrow && (
          <div className="syn-fav-pairs__arrow right" onClick={moveRight}>
            <ArrowLeft />
          </div>
        )}
        <div
          className={classNames('syn-fav-pairs__list-container', {
            left: showLeftArrow,
            right: showRightArrow,
          })}>
          <div
            className={classNames('syn-fav-pairs__list')}
            ref={listRef}
            style={{ transform: `translateX(-${distanceToLeft}px)` }}>
            {displayPairs.map((pair) => {
              return (
                <div
                  key={`${pair.instrumentAddr}-${pair.expiry}-${pair.chainId}`}
                  className="syn-fav-pairs__item"
                  onClick={() => handlePairClick(pair)}>
                  <TokenLogo token={pair.baseToken} size={12} chainId={chainId} />
                  <TokenPairSymbol requiredMarginIcon={false} baseToken={pair.baseToken} quoteToken={pair.quoteToken} />
                  {WrappedBigNumber.from(pair.fairPriceChange24h || 0).formatPercentage({ hundredfold: false })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavPairs;
